import type { Entity, Link } from '@/types/entity';

/**
 * 공유 파생 인덱스 (S3) — 연결성을 한 곳에서 파생하는 순수 프리미티브.
 * coherence·도감 등 모든 뷰가 같은 신호를 읽도록(중복 검출 제거).
 * provenance를 *분리*해 다룬다(명시 링크 vs 산문 멘션 vs 공동등장) — 섞어 가중하지 않음.
 * 계약: 입력은 **active 엔티티/링크**여야 한다(useActive* 훅이 보장; soft-deleted는 호출 전 제외).
 */

/** 명시 링크 차수. */
export function computeDegree(links: Link[]): Map<string, number> {
  const d = new Map<string, number>();
  for (const l of links) {
    d.set(l.from, (d.get(l.from) ?? 0) + 1);
    d.set(l.to, (d.get(l.to) ?? 0) + 1);
  }
  return d;
}

/** incoming 멘션 수: target id → 그를 산문에서 부르는 distinct source 수(self·stale 제외). */
export function computeMentionedBy(entities: Entity[], idSet?: Set<string>): Map<string, number> {
  const ids = idSet ?? new Set(entities.map((e) => e.id));
  const m = new Map<string, number>();
  for (const e of entities) for (const r of new Set(e.refs ?? [])) if (r !== e.id && ids.has(r)) m.set(r, (m.get(r) ?? 0) + 1);
  return m;
}

/** 한 source가 너무 많은 멤버를 묶을 때 쌍 폭발(Σk²) 방지 천장. 초과 source는 공동등장 계산에서 제외. */
const MAX_COOCCUR_MEMBERS = 64;

/**
 * 공동 등장(co-occurrence) — 명시하지 않은 *파생* 관계.
 * **source(비트/노트) 단위**로 함께 묶이는 집합 = 등장인물(characters[]) ∪ 산문 멘션(refs).
 * 한 source 안에서는 같은 쌍을 **1회만** 카운트 → count = "함께 묶은 source(장면·문맥) 수".
 * 대칭 맵(id → 상대 id → count). self·stale id 제외. 거대 source는 천장으로 skip.
 */
export function computeCooccur(entities: Entity[], idSet?: Set<string>): Map<string, Map<string, number>> {
  const ids = idSet ?? new Set(entities.map((e) => e.id));
  const co = new Map<string, Map<string, number>>();
  const bump = (a: string, b: string) => {
    let m = co.get(a);
    if (!m) co.set(a, (m = new Map()));
    m.set(b, (m.get(b) ?? 0) + 1);
  };
  for (const e of entities) {
    // source가 한 문맥에서 함께 부른 멤버 집합(채널 합집합, 쌍당 1회). self·stale 제외.
    const members = new Set<string>();
    if (Array.isArray(e.fields.characters)) {
      for (const x of e.fields.characters as unknown[]) if (typeof x === 'string' && x !== e.id && ids.has(x)) members.add(x);
    }
    for (const r of e.refs ?? []) if (r !== e.id && ids.has(r)) members.add(r);
    if (members.size < 2 || members.size > MAX_COOCCUR_MEMBERS) continue;
    const arr = [...members];
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        bump(arr[i], arr[j]);
        bump(arr[j], arr[i]);
      }
    }
  }
  return co;
}

export interface DerivedGraph {
  degree: Map<string, number>;
  mentionedBy: Map<string, number>;
  cooccur: Map<string, Map<string, number>>;
}

/** 공유 파생 인덱스 한 번에. */
export function deriveGraph(entities: Entity[], links: Link[]): DerivedGraph {
  const idSet = new Set(entities.map((e) => e.id));
  return {
    degree: computeDegree(links),
    mentionedBy: computeMentionedBy(entities, idSet),
    cooccur: computeCooccur(entities, idSet),
  };
}

/** 선택 엔티티의 파생(공동등장) 상대 — exclude(이미 명시 관계)·self 제외, 횟수순. */
export function derivedPartners(graph: DerivedGraph, id: string, exclude: Set<string>): { id: string; count: number }[] {
  const m = graph.cooccur.get(id);
  if (!m) return [];
  return [...m.entries()]
    .filter(([pid]) => pid !== id && !exclude.has(pid))
    .map(([pid, count]) => ({ id: pid, count }))
    .sort((a, b) => b.count - a.count || (a.id < b.id ? -1 : 1));
}
